import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import styled from 'styled-components';
import CloudUpload from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useParams } from 'react-router';
import { ModalDefaultDraggable } from '../../../molecules';
import { setAddGlossaryOpen, setSelectedGlossary } from '../../../redux/slices/wikiSlice';
import { RootState } from '../../../redux/root';
import { useSaveWikiGlossaryMutation, useUpdateGlossaryMutation } from '../../../redux/rtkQuery/apiWikiGlossarySlice';
import { GlossaryDataType } from '../../../api/jurisdiction';

// Register style-based attributors so font, size, and color produce inline CSS,
// which renders correctly in the glossary table without depending on quill.snow.css.
const FONT_WHITELIST = ['Arial', 'Georgia', 'Tahoma', 'Verdana', 'Helvetica', 'Courier', 'sans-serif', 'serif', 'monospace'];
const SIZE_WHITELIST = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px'];

let quillAttributorsRegistered = false;
const registerQuillAttributors = () => {
  if (quillAttributorsRegistered) return;
  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = SIZE_WHITELIST;
  Quill.register(SizeStyle, true);

  const FontStyle = Quill.import('attributors/style/font');
  FontStyle.whitelist = FONT_WHITELIST;
  Quill.register(FontStyle, true);

  Quill.register(Quill.import('attributors/style/color'), true);
  Quill.register(Quill.import('attributors/style/background'), true);
  quillAttributorsRegistered = true;
};
registerQuillAttributors();

const isQuillEmpty = (html: string) => {
  if (!html) return true;
  const stripped = html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim();
  return stripped.length === 0;
};

type ImageEntry = {
  id: string;
  file: File;
  url: string;
};

const AddGlossaryPopup = ({ onSearchChanged }) => {
  // @ts-ignore
  const { wikiId } = useParams();
  const { addGlossaryOpen, selectedGlossary } = useSelector((s: RootState) => s.wikiSlice);
  const [saveWikiGlossary] = useSaveWikiGlossaryMutation();
  const [updateGlossary] = useUpdateGlossaryMutation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<ImageEntry[]>([]);
  const isEditing = Object.keys(selectedGlossary || {}).length > 0;

  const {
    control,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
    reset,
    setValue,
    watch,
  } = useForm({
    shouldUnregister: false,
    defaultValues: { name: '', definition: '' },
    resolver: yupResolver({}),
    mode: 'all',
  });

  const definition = watch('definition');

  useEffect(() => {
    if (selectedGlossary) {
      const incomingDefinition = (selectedGlossary as any).definition || '';
      // Legacy definitions may be plain text with \r\n; convert to <br/> so the
      // rich editor preserves line breaks. New HTML definitions pass through.
      const normalized = incomingDefinition.replace(/\r\n/g, '<br/>');
      reset({
        ...selectedGlossary,
        definition: normalized,
      });
    } else {
      reset({ name: '', definition: '' });
    }
  }, [selectedGlossary]);

  useEffect(() => {
    return () => {
      images.forEach(img => URL.revokeObjectURL(img.url));
    };
  }, [images]);

  const onClose = () => {
    dispatch(setAddGlossaryOpen(false));
    dispatch(setSelectedGlossary({} as GlossaryDataType));
    setImages([]);
  };

  const onBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const onFilesChange = e => {
    if (!e.target.files) return;
    const files: File[] = Array.from(e.target.files);
    const next = files.map(file => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      url: URL.createObjectURL(file),
    }));
    setImages(prev => [...prev, ...next]);
    e.target.value = '';
  };

  const removeImage = (id: string) => {
    setImages(prev => {
      const found = prev.find(p => p.id === id);
      if (found) URL.revokeObjectURL(found.url);
      return prev.filter(p => p.id !== id);
    });
  };

  const onSave = async () => {
    const name: string = (getValues('name') as string) || '';
    const def = (getValues('definition') as string) || '';
    let foundError = false;
    if (!name.trim()) {
      setError('name', { type: 'required', message: 'Name is required!' });
      foundError = true;
    }
    if (isQuillEmpty(def)) {
      setError('definition', { type: 'required', message: 'Definition is required!' });
      foundError = true;
    }
    if (foundError) return;

    const formData = new FormData();
    images.forEach(img => formData.append('uploaded_images', img.file));
    formData.append('name', name);
    formData.append('definition', def);
    formData.append('jurisdiction', wikiId);

    if (!isEditing) {
      await saveWikiGlossary(formData);
      onSearchChanged(name);
    } else {
      await updateGlossary({ form: formData, id: selectedGlossary.id });
    }

    dispatch(setAddGlossaryOpen(false));
    setValue('name', '');
    setValue('definition', '');
    setImages([]);
    dispatch(setSelectedGlossary({} as GlossaryDataType));
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    [],
  );

  const quillFormats = ['font', 'size', 'bold', 'italic', 'underline', 'color', 'background', 'list', 'bullet'];

  if (!addGlossaryOpen) return null;

  return (
    <ModalDefaultDraggable onClose={onClose} title={isEditing ? 'Update Glossary' : 'Add Glossary'} wide>
      <Form>
        <FieldGroup>
          <FieldLabel>
            Name<RedStar>*</RedStar>
          </FieldLabel>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ value, onChange }) => (
              <TextInput
                type="text"
                value={value || ''}
                onChange={ev => {
                  onChange(ev.target.value);
                  if (errors?.name) clearErrors('name');
                }}
                placeholder="Glossary name"
                hasError={!!errors?.name}
              />
            )}
          />
          {errors?.name && <FieldError>{errors.name.message}</FieldError>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            Definition<RedStar>*</RedStar>
          </FieldLabel>
          <Controller
            name="definition"
            control={control}
            defaultValue=""
            render={({ value, onChange }) => (
              <EditorWrap hasError={!!errors?.definition}>
                <ReactQuill
                  theme="snow"
                  value={value || ''}
                  onChange={html => {
                    onChange(html);
                    if (errors?.definition) clearErrors('definition');
                  }}
                  placeholder="Write the definition here..."
                  modules={quillModules}
                  formats={quillFormats}
                />
              </EditorWrap>
            )}
          />
          {errors?.definition && <FieldError>{errors.definition.message}</FieldError>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Samples / Pictures</FieldLabel>
          <UploadDropzone onClick={onBrowseClick}>
            <CloudUpload />
            <UploadText>
              <UploadHeading>Click to upload images</UploadHeading>
              <UploadHint>JPG, PNG, GIF, SVG, WEBP, HEIC, BMP, TIFF</UploadHint>
            </UploadText>
            <input
              ref={fileInputRef}
              multiple
              type="file"
              hidden
              onChange={onFilesChange}
              accept=".xbm,.tif,.jfif,.pjp,.apng,.jpeg,.heif,.ico,.tiff,.webp,.svgz,.jpg,.heic,.gif,.svg,.png,.bmp,.pjpeg,.avif"
            />
          </UploadDropzone>
          {images.length > 0 && (
            <PreviewGrid>
              {images.map(img => (
                <PreviewTile key={img.id}>
                  <RemoveButton type="button" onClick={() => removeImage(img.id)} aria-label="Remove image">
                    <CloseIcon />
                  </RemoveButton>
                  <img src={img.url} alt="" />
                </PreviewTile>
              ))}
            </PreviewGrid>
          )}
        </FieldGroup>

        <Actions>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={onSave}>
            {isEditing ? 'Update' : 'Save'}
          </PrimaryButton>
        </Actions>
      </Form>
    </ModalDefaultDraggable>
  );
};

export default AddGlossaryPopup;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 360px;
  font-family: ${FONT_STACK};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const RedStar = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const FieldError = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const TextInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--mainText);
  font-family: ${FONT_STACK};
  font-size: 0.875rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const fontPickerLabels = FONT_WHITELIST.map(
  font => `
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='${font}']::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='${font}']::before {
      content: '${font}';
      font-family: ${font.includes(' ') ? `'${font}'` : font};
    }
  `,
).join('');

const sizePickerLabels = SIZE_WHITELIST.map(
  size => `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='${size}']::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='${size}']::before {
      content: '${size}';
    }
  `,
).join('');

const EditorWrap = styled.div<{ hasError?: boolean }>`
  ${fontPickerLabels}
  ${sizePickerLabels}

  .ql-toolbar.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-radius: 8px 8px 0 0;
    background: var(--neutral-50);
    padding: 6px 8px;
  }

  .ql-container.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-top: 0;
    border-radius: 0 0 8px 8px;
    background: var(--neutral-0);
    font-family: ${FONT_STACK};
    font-size: 0.875rem;
  }

  .ql-editor {
    min-height: 180px;
    color: var(--mainText);
    line-height: 1.5;
  }

  .ql-editor.ql-blank::before {
    color: var(--neutral-400);
    font-style: normal;
  }

  .ql-snow .ql-picker.ql-font {
    width: 130px;
  }

  .ql-snow .ql-picker.ql-size {
    width: 80px;
  }
`;

const UploadDropzone = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  background: var(--neutral-50);
  border: 1px dashed var(--neutral-300);
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  svg {
    color: var(--primary-600);
    font-size: 28px;
  }

  &:hover {
    border-color: var(--primary-400);
    background: var(--primary-50);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UploadHeading = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const UploadHint = styled.span`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const PreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const PreviewTile = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--neutral-50);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: var(--neutral-0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;

  svg {
    font-size: 14px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecondaryButton = styled.button`
  min-width: 100px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
